import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { resolvePostLoginTarget } from "@/lib/postLogin";
import { useTranslation } from "react-i18next";
import { Loader2, LogIn, AlertTriangle } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import useSEO from "@/hooks/useSEO";

function formatErr(detail) {
  if (!detail) return "Connexion impossible. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  useSEO({
    title: "Connexion — Noam",
    description: "Connexion à la plateforme Noam. Accès réservé aux dirigeants et comités habilités.",
    path: "/login",
  });
  const { user, login } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    try {
      const e = sessionStorage.getItem("noam_google_error");
      if (e) { sessionStorage.removeItem("noam_google_error"); return e; }
    } catch { /* silent */ }
    return null;
  });

  const isAuthed = user && user !== false && user !== null;
  const [redirect, setRedirect] = useState(null);
  useEffect(() => {
    if (!isAuthed) return;
    // Vision Olivier 2026-09 : après connexion (et carrousel), on atterrit
    // TOUJOURS sur l'accueil agentique « Bonjour » — sauf deep-link explicite.
    const fallback = location.state?.from || "/app/noam";
    resolvePostLoginTarget(fallback).then(setRedirect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  if (isAuthed) {
    return redirect ? <Navigate to={redirect} replace /> : null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(email.trim(), password);
      const fallback = location.state?.from || "/app/noam";
      const target = await resolvePostLoginTarget(fallback);
      nav(target, { replace: true });
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--noam-bg)] text-white" data-testid="login-root">
      <div className="absolute inset-0 noam-grid-bg opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] noam-glow pointer-events-none" />

      <header className="relative px-6 pt-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="login-logo">
          <div className="w-9 h-9 rounded-md bg-[#378ADD]/20 border border-[#378ADD]/40 flex items-center justify-center">
            <span className="noam-display font-bold text-[#378ADD]">N</span>
          </div>
          <div className="leading-tight">
            <div className="noam-display text-base font-semibold">Noam</div>
            <div className="text-[10px] text-zinc-500 -mt-0.5">by Constante Partners</div>
          </div>
        </Link>
        <LanguageSwitcher compact />
      </header>

      <main className="relative flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="noam-card p-8">
            <div className="text-[#378ADD] text-[11px] uppercase tracking-[0.18em] font-semibold">Espace pilotage</div>
            <h1 className="noam-display text-2xl font-bold mt-2">Se connecter</h1>
            <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
              Accès réservé aux clients de la plateforme Noam. Les comptes sont créés par votre administrateur.
            </p>

            <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
              <div>
                <label className="text-[11px] text-zinc-400 uppercase tracking-wider font-medium mb-1.5 block">Email</label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="noam-input"
                  placeholder="vous@organisation.com"
                  data-testid="login-email-input"
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="text-[11px] text-zinc-400 uppercase tracking-wider font-medium mb-1.5 block">Mot de passe</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="noam-input"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  autoComplete="current-password"
                />
                <div className="mt-1.5 text-right">
                  <Link to="/forgot-password" className="text-[11px] text-[#378ADD] hover:underline" data-testid="login-forgot-link">
                    {t("auth.forgot_link", { defaultValue: "Mot de passe oublié ?" })}
                  </Link>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs" data-testid="login-error">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email || !password}
                className="btn-primary w-full justify-center disabled:opacity-50"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Connexion…</>
                ) : (
                  <><LogIn className="w-4 h-4" /> Se connecter</>
                )}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-white/5 text-[11px] text-zinc-500 leading-relaxed">
              Vous n'avez pas encore de compte ?{" "}
              <Link to="/contact" className="text-[#378ADD] hover:underline">Demander un accès →</Link>
            </div>
          </div>

          <div className="text-center mt-6 text-[11px] text-zinc-600">
            © 2026 Constante Partners · Genève · Hébergement Suisse
          </div>
        </div>
      </main>
    </div>
  );
}
